// Biometric unlock for Vault Authenticator
// Hardware-backed biometric authentication via WebAuthn API.
// Dual-mode:
//   1. PRF mode (hardware-derived symmetric key from Secure Element if supported)
//   2. Standard mode (WebAuthn hardware userVerification gate + device-bound key)
// In both modes, unlocking REQUIRES a verified physical fingerprint prompt.

const STORAGE_KEY = 'vault_bio_cred'

export async function isBiometricsAvailable() {
  if (!window.PublicKeyCredential) return false
  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
  } catch {
    return false
  }
}

export function hasEnrolledBiometrics() {
  return !!localStorage.getItem(STORAGE_KEY)
}

export function getEnrolledUser() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (parsed.username) return parsed.username
    }
  } catch {}
  return localStorage.getItem('vault_bio_user') || ''
}

export function clearBiometrics() {
  localStorage.removeItem(STORAGE_KEY)
  localStorage.removeItem('vault_bio_user')
}

export async function prfSupported() {
  if (!window.PublicKeyCredential) return false
  try {
    const m = window.PublicKeyCredential
    if (typeof m.getClientCapabilities === 'function') {
      const caps = await m.getClientCapabilities()
      return !!(caps.prf && caps.userVerifyingPlatformAuthenticator)
    }
  } catch { /* fall through */ }
  return false
}

const b64url = (buf) => {
  let s = btoa(String.fromCharCode(...new Uint8Array(buf)))
  return s.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

const fromB64url = (s) => Uint8Array.from(atob(s.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0))

async function aesWrapKey(bytes) {
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return crypto.subtle.importKey('raw', digest, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt'])
}

export async function registerBiometrics(username, masterPw) {
  const challenge = crypto.getRandomValues(new Uint8Array(32))
  const userId = crypto.getRandomValues(new Uint8Array(16))
  const anchor = crypto.getRandomValues(new Uint8Array(32))

  let credential
  try {
    credential = await navigator.credentials.create({
      publicKey: {
        challenge,
        rp: { name: 'Vault Authenticator', id: window.location.hostname },
        user: {
          id: userId,
          name: username || 'vault_user',
          displayName: username || 'Vault User'
        },
        pubKeyCredParams: [
          { alg: -7, type: 'public-key' },   // ES256 (universal for Android/iOS)
          { alg: -257, type: 'public-key' }, // RS256
          { alg: -8, type: 'public-key' }    // Ed25519
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          userVerification: 'required', // force biometric prompt
          residentKey: 'discouraged'
        },
        timeout: 60000,
        extensions: {
          prf: { eval: { first: anchor } }
        }
      }
    })
  } catch (e) {
    throw new Error('Biometric setup cancelled or failed: ' + (e.message || 'error'))
  }
  if (!credential) throw new Error('Biometric setup cancelled')

  const clientExt = credential.getClientExtensionResults ? credential.getClientExtensionResults() : {}
  const credId = new Uint8Array(credential.rawId)

  let mode = 'standard'
  let keyBytes = null

  // 1. If PRF evaluated directly in create():
  if (clientExt?.prf?.results?.first) {
    mode = 'prf'
    keyBytes = new Uint8Array(clientExt.prf.results.first)
  } else if (clientExt?.prf?.enabled) {
    // 2. PRF extension enabled, fetch key via get():
    try {
      const assertion = await navigator.credentials.get({
        publicKey: {
          challenge: anchor,
          rpId: window.location.hostname,
          allowCredentials: [{ id: credId, type: 'public-key' }],
          userVerification: 'required',
          timeout: 60000,
          extensions: { prf: { eval: { first: anchor } } }
        }
      })
      const prfResult = assertion?.getClientExtensionResults?.()?.prf?.results?.first
      if (prfResult) {
        mode = 'prf'
        keyBytes = new Uint8Array(prfResult)
      }
    } catch {
      // Fallback to standard
    }
  }

  // 3. Standard mode: hardware WebAuthn gate + device-bound key
  let deviceSalt = null
  if (!keyBytes) {
    mode = 'standard'
    deviceSalt = crypto.getRandomValues(new Uint8Array(32))
    const rawKeyMaterial = new Uint8Array(deviceSalt.length + credId.length)
    rawKeyMaterial.set(deviceSalt, 0)
    rawKeyMaterial.set(credId, deviceSalt.length)
    keyBytes = rawKeyMaterial
  }

  const key = await aesWrapKey(keyBytes)
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const enc = new TextEncoder().encode(masterPw)
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, enc)

  if (username) {
    localStorage.setItem('vault_bio_user', username)
  }

  localStorage.setItem(STORAGE_KEY, JSON.stringify({
    username: username || '',
    mode,
    id: b64url(credId),
    anchor: b64url(anchor),
    deviceSalt: deviceSalt ? b64url(deviceSalt) : null,
    iv: b64url(iv),
    data: b64url(new Uint8Array(ciphertext))
  }))

  return true
}

export async function unlockWithBiometrics() {
  const raw = localStorage.getItem(STORAGE_KEY)
  if (!raw) throw new Error('Biometrics not registered on this device')
  const record = JSON.parse(raw)

  const credId = fromB64url(record.id)
  const anchor = fromB64url(record.anchor)

  // Trigger hardware biometric prompt (fingerprint sensor)
  let assertion
  try {
    assertion = await navigator.credentials.get({
      publicKey: {
        challenge: anchor,
        rpId: window.location.hostname,
        allowCredentials: [{ id: credId, type: 'public-key' }],
        userVerification: 'required', // force fingerprint
        timeout: 60000,
        extensions: record.mode === 'prf' ? { prf: { eval: { first: anchor } } } : undefined
      }
    })
  } catch (e) {
    throw new Error('Fingerprint verification cancelled or failed')
  }
  if (!assertion) throw new Error('Fingerprint verification failed')

  let keyBytes
  if (record.mode === 'prf') {
    const ext = assertion.getClientExtensionResults ? assertion.getClientExtensionResults() : {}
    const prfKey = ext?.prf?.results?.first
    if (!prfKey) throw new Error('PRF key retrieval failed')
    keyBytes = new Uint8Array(prfKey)
  } else {
    // Standard mode: hardware verified identity
    const deviceSalt = fromB64url(record.deviceSalt)
    const rawKeyMaterial = new Uint8Array(deviceSalt.length + credId.length)
    rawKeyMaterial.set(deviceSalt, 0)
    rawKeyMaterial.set(credId, deviceSalt.length)
    keyBytes = rawKeyMaterial
  }

  const key = await aesWrapKey(keyBytes)
  const iv = fromB64url(record.iv)
  const data = fromB64url(record.data)
  const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, data)
  const masterPw = new TextDecoder().decode(decrypted)
  const username = record.username || localStorage.getItem('vault_bio_user') || ''
  return {
    masterPw,
    username,
    toString: () => masterPw
  }
}
